import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  PreferenceSlot,
  Section,
  SectionSlot,
  SlotLabel,
  SupportedLocale,
} from '@youshift/shared/types';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useDeletePrefSlots } from '@youshift/shared/hooks/mutations';
import toast from 'react-hot-toast';
import { DragOverlay } from '@dnd-kit/core';

import Modal from '../../../../components/Modal';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import { PreferenceSlotSelector } from './PreferenceSlotSelector';
import { CompactSectionSlotChip } from '../../../../components/Calendars/SectionSlotChip';
import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';
import { getNewPrefSlotsFromSectionSlots } from './utils';
import { SortableOrphanSlot } from './SortableOrphanSlot';
import { YSButton } from '../../../../components/Buttons';

interface PrefSlotsCalendarToolbarProps {
  orphanSlots: Record<number, SectionSlot>;
  locale: SupportedLocale;
  selectedPrefSlots: Set<number>;
  onSelectAllPrefSlots?: () => void;
  onClearPrefSlots?: () => void;
  activeId: number | null;
  setTemporaryPrefSlots: Dispatch<
    SetStateAction<Record<number, PreferenceSlot>>
  >;
  selectedTemporaryPrefSlots: Set<number>;
  createPreferenceSlotModalOpen: boolean;
  setCreatePreferenceSlotModalOpen: Dispatch<SetStateAction<boolean>>;
  temporaryPrefSlots: Record<number, PreferenceSlot>;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
}

export function PrefSlotsCalendarToolbar({
  orphanSlots,
  setHasChanges,
  locale,
  selectedPrefSlots,
  onSelectAllPrefSlots,
  onClearPrefSlots,
  activeId,
  setTemporaryPrefSlots,
  selectedTemporaryPrefSlots,
  createPreferenceSlotModalOpen,
  setCreatePreferenceSlotModalOpen,
  temporaryPrefSlots,
}: PrefSlotsCalendarToolbarProps) {
  const { t } = useTranslation();
  const { idItr } = useParams();
  const { sectionsWithSlots, sectionSlotsDict } = useItrContext();
  const queryClient = useQueryClient();
  const [
    bulkDeletePreferenceSlotModalOpen,
    setBulkDeletePreferenceSlotModalOpen,
  ] = useState(false);

  // State for selected section slots to create pref slots
  const [selectedSectionSlots, setSelectedSectionSlots] = useState<Set<number>>(
    new Set(),
  );
  const [selectedPrefLabel, setSelectedPrefLabel] = useState<number | null>(
    null,
  );

  const clearForm = () => {
    setSelectedSectionSlots(new Set());
    setSelectedPrefLabel(null);
  };

  const deletePrefSlotsMutation = useDeletePrefSlots(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      setBulkDeletePreferenceSlotModalOpen(false);
      toast.success(
        t('manager.preferencesConfig.deletePreferenceSlotsSuccess'),
      );
    },
  });

  const handlePreviewPreferenceSlots = () => {
    if (!selectedPrefLabel || !idItr) return;

    clearForm();

    const selectedSlotsToCreatePrefSlots = Object.fromEntries(
      Object.entries(sectionSlotsDict).filter(([_, slot]) =>
        selectedSectionSlots.has(slot.id_section_slot),
      ),
    );
    const newTemporaryPrefSlots = getNewPrefSlotsFromSectionSlots(
      selectedSlotsToCreatePrefSlots,
      selectedPrefLabel,
      Number(idItr),
    );
    setTemporaryPrefSlots(newTemporaryPrefSlots);
    setHasChanges(true);
    setCreatePreferenceSlotModalOpen(false);
  };

  const handleDeletePreferenceSlots = () => {
    if (selectedPrefSlots.size === 0 && selectedTemporaryPrefSlots.size === 0)
      return;

    if (selectedPrefSlots.size > 0) {
      deletePrefSlotsMutation.mutate({
        id_itr: idItr,
        id_pref_slots: Array.from(selectedPrefSlots),
      });
    }

    if (selectedTemporaryPrefSlots.size > 0) {
      setTemporaryPrefSlots(
        Object.fromEntries(
          Object.entries(temporaryPrefSlots).filter(([id_pref_slot]) => {
            const idPrefSlotNumber = id_pref_slot as unknown as number;
            return !selectedTemporaryPrefSlots.has(idPrefSlotNumber);
          }),
        ) as Record<number, PreferenceSlot>,
      );
    }
    onClearPrefSlots?.();
    setBulkDeletePreferenceSlotModalOpen(false);
  };

  const sections: Record<number, Section> = useMemo(
    () =>
      sectionsWithSlots.reduce<Record<number, Section>>((acc, { section }) => {
        acc[section.id_section] = section;
        return acc;
      }, {}),
    [sectionsWithSlots],
  );

  return (
    <div className="flex flex-col h-full border-l border-gray-200 p-3 w-64">
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-sm font-medium">
          {t('manager.preferencesConfig.orphanSlots')}
          {` (${Object.keys(orphanSlots).length})`}
        </h3>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        {t('manager.preferencesConfig.dragOrphanSlots')}
      </p>

      <div className="overflow-y-auto flex-grow space-y-2 max-h-[30vh]">
        {Object.keys(orphanSlots).length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            {t('manager.preferencesConfig.noOrphanSlots')}
          </p>
        ) : (
          Object.values(orphanSlots)
            .sort(
              (a, b) =>
                new Date(a.start).getTime() - new Date(b.start).getTime(),
            )
            .map(slot => (
              <SortableOrphanSlot
                key={slot.id_section_slot}
                id={slot.id_section_slot}
                sectionSlot={slot}
                section={sections[slot.id_section]}
                showDay
                locale={locale}
              />
            ))
        )}
      </div>
      <DragOverlay>
        {activeId ? (
          <CompactSectionSlotChip
            sectionSlot={orphanSlots[activeId]}
            section={sections[orphanSlots[activeId].id_section]}
            showDay
            locale={locale}
          />
        ) : null}
      </DragOverlay>

      <div className="mt-4 border-t pt-3 flex flex-col gap-2 justify-start">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium">
            {t('manager.preferencesConfig.preferenceSlots')}
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onSelectAllPrefSlots}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {t('generic.selectAll')}
          </button>
          <button
            onClick={onClearPrefSlots}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {t('generic.clear')}
          </button>
        </div>
        <button
          onClick={() => setCreatePreferenceSlotModalOpen(true)}
          className="flex items-center justify-center p-1.5 rounded-md hover:bg-gray-100 border border-gray-200"
        >
          <PlusIcon className="h-5 w-5 text-gray-600" />
          <span className="ml-1 text-sm">
            {t('manager.preferencesConfig.addPreferenceSlots')}
          </span>
        </button>
        <button
          onClick={() => setBulkDeletePreferenceSlotModalOpen(true)}
          className="flex items-center justify-center p-1.5 rounded-md hover:bg-gray-100 border border-gray-200"
        >
          <TrashIcon className="h-5 w-5 text-gray-600" />
          <span className="ml-1 text-sm">
            {t('manager.preferencesConfig.deletePreferenceSlots')}
          </span>
        </button>
      </div>

      {/* Add Preference Slot Modal */}
      <Modal
        isOpen={createPreferenceSlotModalOpen}
        onClose={() => setCreatePreferenceSlotModalOpen(false)}
        size="xl"
      >
        <div>
          <h2 className="text-lg font-medium mb-4">
            {t('manager.preferencesConfig.addPreferenceSlot')}
          </h2>
          <PreferenceSlotSelector
            orphanSlots={orphanSlots}
            selectedSlots={selectedSectionSlots}
            setSelectedSlots={setSelectedSectionSlots}
            selectedPrefLabel={selectedPrefLabel}
            setSelectedPrefLabel={setSelectedPrefLabel}
          />
          <div className="flex justify-end space-x-2 mt-2">
            <YSButton
              variant="ghost-secondary"
              onClick={() => {
                setCreatePreferenceSlotModalOpen(false);
                clearForm();
              }}
            >
              {t('generic.cancel')}
            </YSButton>
            <YSButton
              variant="primary"
              onClick={handlePreviewPreferenceSlots}
              disabled={selectedSectionSlots.size === 0 || !selectedPrefLabel}
            >
              {t('generic.preview')}
            </YSButton>
          </div>
        </div>
      </Modal>

      {/* Delete Preference Slots Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={bulkDeletePreferenceSlotModalOpen}
        onClose={() => setBulkDeletePreferenceSlotModalOpen(false)}
        onConfirm={handleDeletePreferenceSlots}
        title={t('manager.preferencesConfig.deletePreferenceSlots')}
        description={
          selectedPrefSlots.size === 0 && selectedTemporaryPrefSlots.size === 0
            ? t('manager.preferencesConfig.noPreferenceSlotsSelected')
            : t('manager.preferencesConfig.deleteConfirmation', {
                count:
                  (selectedPrefSlots.size || 0) +
                  (selectedTemporaryPrefSlots.size || 0),
              })
        }
      />
    </div>
  );
}
